import InfiniteScroll from "react-infinite-scroll-component";
import { CertificateFilters, CertificateListView, downloadCertificate } from "@/api/williams/certificates";
import { ExpandableCard, PinButton, useSelectedItems } from "../../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { getStandardName } from "../../standardTests/lookup/standard";
import { highlight } from "@/util";
import { Link } from "react-router-dom";
import { useCertificateList, usePinCertificate } from "../hooks";
import { useState } from "react";

export type CertificationsProps = {
  filters: Partial<CertificateFilters>;
  expanded?: boolean;
  allowExpand?: boolean;
};

export function Certifications({ filters, ...props }: CertificationsProps) {
  const certificates = useCertificateList(filters);

  const data = certificates.data?.pages.flatMap((page) => page.results) ?? [];
  const total = certificates.data?.pages[0]?.total ?? 0;

  return (
    <ExpandableCard caption={`Certifications (${total})`} expanded={props.expanded} allowExpand={props.allowExpand}>
      <InfiniteScroll
        dataLength={data.length}
        next={certificates.fetchNextPage}
        hasMore={Boolean(certificates.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {certificates.isLoading ? (
          <p>Loading...</p>
        ) : certificates.isError ? (
          <p>An unknown error occurred.</p>
        ) : certificates.isSuccess && data.length > 0 ? (
          <CertificationsTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No certifications found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

export function CertificationsTable({ data, search }: { data: CertificateListView[]; search?: string }) {
  const allowSelection = useSelectedItems<CertificateListView>();

  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          {allowSelection && <th className="col-auto" />}
          <th className="col col-md-4">Record Number</th>
          <th className="col-4 d-none d-md-table-cell">Standard</th>
          <th className="col-4 d-none d-md-table-cell">Welder</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((certification) => (
          <CertificationsRow key={certification.recordId} certification={certification} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function CertificationsRow({ certification, search }: { certification: CertificateListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const pin = usePinCertificate();
  const selected = useSelectedItems<CertificateListView>();

  function handleSelectCertificate(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      selected.add(certification);
    } else {
      selected.remove((x) => x.recordId === certification.recordId);
    }
  }

  return (
    <>
      <tr key={`${certification.recordId}_${certification.pinned}`}>
        {selected && (
          <td className="ps-0" style={{ width: 0 }}>
            <input
              type="checkbox"
              className="form-check-input"
              checked={selected.items.some((x) => x.recordId === certification.recordId)}
              onChange={handleSelectCertificate}
            />
          </td>
        )}
        <td>
          <a className="link-primary" onClick={() => downloadCertificate(certification.recordId)}>
            {highlight(certification.recordNumber, search)}
          </a>
        </td>
        <td className="d-none d-md-table-cell">{getStandardName(certification.standard)}</td>
        <td className="d-none d-md-table-cell">
          <Link to={getFullPath(routes.welder.open(certification.welderId))} className="link link-primary">
            {highlight(certification.welderName, search)}
          </Link>
        </td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={certification.pinned} onChange={() => pin.mutate(certification.recordId)} />
        </td>
      </tr>
      {expanded && (
        <tr>
          {selected && <td />}
          <td className="d-none d-md-table-cell" colSpan={4}>
            <ExpandedDetails certification={certification} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails certification={certification} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ certification, search }: { certification: CertificateListView; search?: string }) {
  return (
    <div className="ms-1">
      <div className="bg-light p-1">Certification Details</div>
      <div className="p-1">
        <dl>
          <dt className="d-block d-md-none">Welder</dt>
          <dd className="d-block d-md-none">
            <Link to={getFullPath(routes.welder.open(certification.welderId))} className="link-primary">
              {highlight(certification.welderName, search)}
            </Link>
          </dd>
          <dt className="d-block d-md-none">Standard</dt>
          <dd className="d-block d-md-none">{getStandardName(certification.standard)}</dd>
          {certification.process && (
            <>
              <dt>Process</dt>
              <dd>{highlight(certification.process, search)}</dd>
            </>
          )}
          <dt>Result</dt>
          <dd>{certification.result}</dd>
          {certification.templateId && (
            <>
              <dt>Standard Test</dt>
              <dd>
                <a className="link-primary" onClick={() => downloadCertificate(certification.templateId)}>
                  {highlight(certification.templateNumber, search)}
                </a>
              </dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
